@if (transaction$ | async) {
  <div class="transaction-details-page mt-5">
    <div class="transaction-details">
      <div class="entity-details-container">
        <ng-container *ngTemplateOutlet="transactionDetails; context: { $implicit: (transaction$ | async)?.transactionDetails }" />
      </div>
    </div>

    <div class="history-column">
      <app-transaction-history
        [transactionHistoryItems]="(transaction$ | async)?.transactionHistory || []"
        [loading]="(transaction$ | async)?.transactionHistory === null"
      />
    </div>
  </div>
}

<ng-template #transactionDetails [appTemplateContext]="transactionDetailsContext" let-transaction>
  <div class="details-section-title">
    <div class="title">Transaction Details</div>
  </div>

  <div class="grid gap-y-5 w-full xl:w-3/3 lg:grid-cols-3">
    <app-details-item-id label="Transaction ID" [id]="transaction.id" />

    <app-details-item-id
      label="Business Account ID"
      [link]="['/app/business-accounts', transaction.businessAccountId]"
      [id]="transaction.businessAccountId"
    />
  </div>

  <div class="separator-row"></div>

  <div class="amount-row">
    <div class="amount-label">Amount</div>
    <div class="amount-value">{{ transaction.amount | amount: transaction.currency }} {{ transaction.currency }}</div>
  </div>

  <div class="from-to-row">
    <div class="from-to-item">
      <div class="title">From Account</div>
      <ng-container *ngTemplateOutlet="fromToAccount; context: { $implicit: transaction.debitFinancialAccount, transaction }" />
    </div>

    <div class="from-to-item">
      <div class="title">To Account</div>
      <ng-container *ngTemplateOutlet="fromToAccount; context: { $implicit: transaction.creditFinancialAccount, transaction }" />
    </div>
  </div>

  <div class="additional-details">
    <app-details-item class="transaction-type" label="Transaction Type" [value]="transaction.transactionType" />

    <app-details-item label="Status Message" [value]="transaction.latestStatus.message" />

    <app-details-item-id label="Initiator Account ID" [id]="transaction.initiatorAccountHolderId" />

    <app-details-item-id label="External Transaction Initiator ID" [id]="transaction.externalTransactionInitiatorId" />

    <app-details-item label="Debit Authorization ID" [value]="transaction.debitAuthorizationId || '' | rktShortId" />

    @if (transaction.scheduledTransactionId) {
      <app-details-item label="Schedule ID" [value]="transaction.scheduledTransactionId || '' | rktShortId" />
    }

    <app-details-item label="Transaction Origin" [value]="transaction.originatingChannel" />
    <app-details-item label="Payment Reason" [value]="transaction.paymentReason" />
    <app-details-item label="Settlement Priority" [value]="transaction.settlementPriority" />
    <app-details-item label="Solution" [value]="transaction.solution | titlecase" />
    <app-details-item label="Currency" [value]="transaction.currency" />
  </div>

  <div class="transaction-metadata">
    <div
      class="transaction-metadata-title"
      (click)="isShowMetadata = !isShowMetadata"
      (keydown.enter)="isShowMetadata = !isShowMetadata"
      tabindex="0"
    >
      <div class="title">Metadata</div>
      <rkt-icon [name]="isShowMetadata ? 'chevron-down' : 'chevron-right'" />
    </div>

    @if (isShowMetadata) {
      <div class="transaction-metadata-items">
        <div class="transaction-metadata-item transaction-metadata-header-item">
          <div class="key">Key</div>

          <div class="value">Value</div>
        </div>

        @if (!transaction.metadataItems.length) {
          <div class="transaction-metadata-empty-item">No Transaction Metadata</div>
        }

        @for (metadataItem of transaction.metadataItems; track $index) {
          <div class="transaction-metadata-item">
            <div class="key">{{ metadataItem.key }}</div>

            <div class="value">
              {{ metadataItem.value }}
            </div>
          </div>
        }
      </div>
    }
  </div>

  <div class="create-update-details">
    <app-details-item label="Created At" [value]="transaction.createdAt | dateFormat" />

    <app-details-item label="Created By" [value]="transaction.createdBy.id | teamMemberName | async" />

    <app-details-item label="Updated At" [value]="transaction.updatedAt | dateFormat" />
  </div>
</ng-template>

<ng-template #fromToAccount let-financialAccount let-transaction="transaction">
  <div class="item-body">
    <div class="item-body-row">
      <div class="label">Account Holder</div>
      <div class="value">{{ financialAccount?.accountHolderType | notAvailable }}</div>
    </div>

    <ng-container
      *ngTemplateOutlet="
        itemBodyRows;
        context: {
          $implicit: [
            { value: financialAccount?.accountInfo, label: 'Account Info', splitable: true },
            { value: financialAccount?.type, label: 'Account Type' },
            { value: financialAccount?.subtype, label: 'Account Subtype' },
            { value: financialAccount?.category, label: 'Account Category' },
            {
              value: financialAccount?.address,
              label: 'Address',
              className:
                (!!financialAccount?.bankAccount || !!financialAccount?.token ? 'item-body-row-with-separator ' : '') +
                'item-body-row-tall',
              type: 'address',
            },
          ],
        }
      "
    >
    </ng-container>

    @if (financialAccount?.bankAccount) {
      <div class="item-body-row">
        <div class="label">Routing No.</div>
        <div class="value">
          {{ financialAccount?.bankAccount?.routingNo | notAvailable }}
        </div>
      </div>
    }

    @if (transaction.debitFinancialAccountId === financialAccount?.id && transaction.isMillikartSolution && financialAccount?.token) {
      <div class="item-body-row">
        <div class="label">Token</div>
        <div class="value">
          {{ financialAccount?.token }}
        </div>
      </div>
    }

    @if (transaction.creditFinancialAccountId === financialAccount?.id && transaction.isMillikartSolution && financialAccount?.token) {
      <div class="item-body-row">
        <div class="label">Token</div>
        <div class="value">
          {{ financialAccount?.token }}
        </div>
      </div>
    }
  </div>
</ng-template>

<ng-template #itemBodyRows let-rows>
  @for (row of rows; track $index) {
    <div class="item-body-row" [class.empty-value]="!row.value" [ngClass]="row.className">
      @if (row.label) {
        <div class="label">
          {{ row.label }}
        </div>
      }

      @if (row.type !== 'address' || (row.type === 'address' && !row.value)) {
        <div class="value">
          @if (row.splitable) {
            <div [class.empty-value]="!(row.value || '' | rktSplit: '::' : 0)">
              {{ row.value || '' | rktSplit: '::' : 0 | notAvailable }}
            </div>
            <div [class.empty-value]="!(row.value || '' | rktSplit: '::' : 1)">
              {{ row.value || '' | rktSplit: '::' : 1 | notAvailable }}
            </div>
          } @else {
            {{ row.value | notAvailable }}
          }
        </div>
      }

      @if (row.type === 'address' && row.value) {
        <div class="value">
          {{ row.value?.addressLine1 }},<br />
          @if (row.value?.addressLine2) {
            {{ row.value?.addressLine2 }}<br />
          }
          {{ row.value?.city }}, {{ row.value?.state }}<br />
          {{ row.value?.postalCode }}
          {{ row.value?.country }}
        </div>
      }
    </div>
  }
</ng-template>
